import { FC, ReactNode, useRef } from 'react';
import { Button, Col, Input, InputRef, Row, Typography } from 'antd';
import { TbSearch, TbSlash, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';

interface WorkspaceResourceListPageHeaderProps {
    action?: ReactNode;
    label: string;
    onSearchChange: (value: string) => void;
    searchDisabled?: boolean;
    searchValue: string;
}

/** Shared title, slash-focus search, and action row for workspace-resource list routes. */
const WorkspaceResourceListPageHeader: FC<WorkspaceResourceListPageHeaderProps> = ({
    action,
    label,
    onSearchChange,
    searchDisabled = false,
    searchValue,
}) => {
    const { token } = useAntDesignTheme();
    const inputRef = useRef<InputRef>(null);

    useSlashSearchFocus(inputRef);

    return (
        <Row
            align='middle'
            data-workspace-resource-list-page-header='true'
            gutter={[16, 8]}
            style={{ borderBottom: `1px solid ${token.colorBorder}`, padding: '0 1.5rem 1rem' }}
        >
            <Col xs={24} md={6}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                    {label}
                </Typography.Title>
            </Col>
            <Col xs={24} md={12}>
                <Input
                    disabled={searchDisabled}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={`Search ${label}...`}
                    prefix={<TbSearch style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }} />}
                    ref={inputRef}
                    suffix={
                        searchValue ? (
                            <Button
                                aria-label={`Clear ${label} Search`}
                                icon={<TbX size='0.875rem' style={{ color: token.colorTextQuaternary }} />}
                                onClick={() => onSearchChange('')}
                                size='small'
                                style={{ height: '1rem', width: '1rem' }}
                                type='text'
                            />
                        ) : (
                            <TbSlash size='1rem' style={{ color: token.colorTextQuaternary, marginRight: 0 }} />
                        )
                    }
                    value={searchValue}
                />
            </Col>
            <Col xs={24} md={6} style={{ textAlign: 'right' }}>
                {action}
            </Col>
        </Row>
    );
};

export default WorkspaceResourceListPageHeader;
